import * as Sentry from '@sentry/react';
// @ts-ignore
import { notification } from 'antd';
import { fileTypeFromBuffer } from 'file-type/core';
import { createContext, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import FilesUploadErrorForm from '../components/Forms/FilesUploadErrorForm';
import FileUploadOptionsForm from '../components/Forms/FileUploadOptionsForm';
import { EventBusEventEnum } from '../enums/EventBusEventEnum';
import { FileUploadStatusEnum } from '../enums/FileUploadStatusEnum';
import { UploadFileActionEnum } from '../enums/UploadFileActionEnum';
import { useAuth } from '../hooks/useAuth';
import { useModal } from '../hooks/useModal';
import { usePermissions } from '../hooks/usePermissions';
import type FileInterface from '../interfaces/File';
import type { CreateFolderPayload, UpdateFilesAccessPayload } from '../interfaces/FileService';
import type Group from '../interfaces/Group';
import type GroupedUploadItem from '../interfaces/GroupedUploadItem';
import type UploadFile from '../interfaces/UploadFile';
import type User from '../interfaces/User';
import EventBus from '../services/EventBus';
import { FileService } from '../services/fileService';
import { TransferService } from '../services/transferService';
import { dateToTicks, generateUUID } from '../services/utils';

interface GroupedExistingFile extends FileInterface {
  quantity: number;
}

interface FileUploadContextType {
  uploadFiles: (
    items: FileList | DataTransferItemList | UploadFile[],
    skipTreeBuild?: boolean,
    folderUid?: number,
    cufi?: string,
  ) => void;
  uploadFilesQueue: UploadFile[];
  uploadGroupedFilesQueue: GroupedUploadItem[];
  uploadQuickShareGroupedFilesQueue: GroupedUploadItem[];
  uploadQuickShareGroupedFilesQueueWithoutRootFolder: GroupedUploadItem[];
  uploadingFilesAmount: number;
  uploadedFilesAmount: number;
  totalGroupedFilesAmount: number;
  totalQuickShareGroupedFilesAmount: number;
  uploadedGroupedFilesAmount: number;
  uploadedQuickShareGroupedFilesAmount: number;
  failedGroupedFilesAmount: number;
  isUploadingFiles: boolean;
  isUploadingQuickShareFiles: boolean;
  areFoldersPresentsInFilesQueue: boolean;
  getFailedFilesInFolder: (uuid: string) => UploadFile[];
  isFileInsideFolder: (file: UploadFile) => boolean;
  buildFileTreeFromUploadFiles: (files: UploadFile[]) => UploadFile[];
  quickShareForm: QuickShareFormState;
  getFilesToEnqueue: (
    fileTree: UploadFile[],
    parentUid?: number | null,
    parentUUID?: string | null,
  ) => UploadFile[];
  groupUploadItems: (
    files: UploadFile[],
    onlyQuickShare?: boolean,
    startDepth?: number,
  ) => GroupedUploadItem[];
  emptyUploadFilesQueue: () => void;
  emptyQuickShareUploadFilesQueue: () => void;
  getFileTree: (items: FileList | DataTransferItemList) => Promise<UploadFile[]>;
  onNewFileUploaded: (callback: (file: FileInterface, isReplacing: boolean) => void) => void;
  offNewFileUploaded: (callback: (file: FileInterface, isReplacing: boolean) => void) => void;
  handleQuickShareInputChange: (field: keyof QuickShareFormState, value: any) => void;
  handleShareQuickShareFiles: () => void;
  failedUploadQuickShareFiles: UploadFile[];
  handleOpenUploadingFilesErrorsModal: (onlyQuickShareFiles: boolean) => void;
  retryFailedUploads: (onlyQuickShare?: boolean) => void;
  isQuickShareUploadFilesCompletedWithoutErrors: boolean;
  setCurrentFolderUid: (uid: number | null) => void;
  quickShareGlobalProgressByBytes: number;
}

interface QuickShareFormState {
  users: User[];
  groups: Group[];
  permission: string;
  expiration: string;
  hasMessage: boolean;
  message: string;
}

type UploadResponse = { cufi: string; version: number } | FileInterface;

const transferService = new TransferService();
const fileService = new FileService();

export const FileUploadContext = createContext<FileUploadContextType>({
  uploadFiles: () => {},
  uploadFilesQueue: [],
  uploadGroupedFilesQueue: [],
  uploadQuickShareGroupedFilesQueue: [],
  uploadQuickShareGroupedFilesQueueWithoutRootFolder: [],
  uploadingFilesAmount: 0,
  uploadedFilesAmount: 0,
  totalGroupedFilesAmount: 0,
  uploadedGroupedFilesAmount: 0,
  uploadedQuickShareGroupedFilesAmount: 0,
  totalQuickShareGroupedFilesAmount: 0,
  failedGroupedFilesAmount: 0,
  isUploadingFiles: false,
  isUploadingQuickShareFiles: false,
  buildFileTreeFromUploadFiles: () => [],
  areFoldersPresentsInFilesQueue: false,
  getFilesToEnqueue: () => [],
  groupUploadItems: () => [],
  getFailedFilesInFolder: () => [],
  isFileInsideFolder: () => false,
  emptyUploadFilesQueue: () => {},
  emptyQuickShareUploadFilesQueue: () => {},
  getFileTree: async () => [],
  onNewFileUploaded: () => {},
  offNewFileUploaded: () => {},
  quickShareForm: {
    users: [],
    groups: [],
    permission: '0',
    expiration: '1',
    hasMessage: true,
    message: '',
  },
  handleQuickShareInputChange: () => {},
  handleShareQuickShareFiles: () => {},
  failedUploadQuickShareFiles: [],
  handleOpenUploadingFilesErrorsModal: () => {},
  retryFailedUploads: () => {},
  isQuickShareUploadFilesCompletedWithoutErrors: false,
  setCurrentFolderUid: () => {},
  quickShareGlobalProgressByBytes: 0,
});

let currentFolderUid: number | null = null;

/**
 * Provides functionality for uploading files within the application, including
 * building a hierarchical file tree, processing entries, and managing files in queues.
 *
 * @param {Object} props - The properties passed to the FileUploadProvider component.
 * @param {ReactNode} props.children - React children elements rendered within the provider.
 * @returns {JSX.Element} Returns a React component that wraps children and provides file upload contexts.
 */
export const FileUploadProvider = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation();
  const { isLoggedIn, onBehalfOfUser } = useAuth();
  const { fileSizeLimitUpload } = usePermissions();
  const location = useLocation();
  const modal = useModal();

  const [uploadFilesQueue, setUploadFilesQueue] = useState<UploadFile[]>([]);

  const [quickShareForm, setQuickShareForm] = useState<QuickShareFormState>({
    users: [],
    groups: [],
    permission: '0',
    expiration: '1',
    hasMessage: true,
    message: '',
  });

  const [showOnlyQuickShareFilesUploadingErrors, setShowOnlyQuickShareFilesUploadingErrors] =
    useState(false);

  const newFileUploadedSubscribers = useRef(
    new Set<(file: FileInterface, isReplacing: boolean) => void>(),
  );

  let uploadOptionAction = useRef<UploadFileActionEnum>(UploadFileActionEnum.Replace);
  let uploadOptionGroupedExistingFiles = useRef<Record<string, GroupedExistingFile>>({});

  const MAX_CONCURRENT_UPLOADS = 5; // Maximum number of concurrent uploads
  const CHUNK_SIZE = 10 * 1024 * 1024; // Default chunk size of 10MB

  function setCurrentFolderUid(uid: number | null) {
    currentFolderUid = uid;
  }

  /**
   * Constructs a hierarchical file tree from an array of File objects.
   *
   * The function processes the provided File objects and builds a nested
   * structure representing the directory/file hierarchy. Each node in the
   * tree is represented as an UploadFile object, which contains metadata
   * and children nodes for directories.
   *
   * @param {File[]} files - An array of File objects, which may contain
   *                         relative paths indicating their position in the
   *                         directory structure.
   * @returns {UploadFile[]} A nested array of UploadFile objects representing
   *                         the folder/file hierarchy.
   */
  const buildFileTree = (files: File[]): UploadFile[] => {
    const root: UploadFile[] = [];

    const batch = generateUUID();
    files.forEach((file) => {
      const relativePath = file.webkitRelativePath || file.name;
      const parts = relativePath.split('/');
      let currentLevel = root;

      parts.forEach((part, index) => {
        const isFile = index === parts.length - 1;
        const existing = currentLevel.find((item) => item.fileName === part);

        if (existing) {
          currentLevel = existing.children!;
        } else {
          const newItem: UploadFile = {
            id: generateUUID(),
            fileName: part,
            isFolder: !isFile,
            file: isFile ? file : undefined,
            progress: 0,
            status: FileUploadStatusEnum.Pending,
            isQuickShare: location.pathname === '/quick-share',
            retries: 0,
            children: [],
            batch,
          };
          currentLevel.push(newItem);
          if (newItem.isFolder) {
            currentLevel = newItem.children!;
          }
        }
      });
    });

    return root;
  };

  /**
   * Processes a given FileSystemEntry, retrieving all files contained within it.
   *
   * @param {FileSystemEntry} entry - The file system entry to process. It could be either a file or a directory.
   * @param {string} [path=''] - An optional path prefix to append to the file names.
   * @returns {Promise<File[]>} A promise that resolves with an array of File objects representing the files within the entry.
   *                            If the entry is a directory, it will recursively retrieve files from all nested subdirectories.
   */
  const processEntry = (entry: FileSystemEntry, path: string = ''): Promise<File[]> => {
    return new Promise((resolve, reject) => {
      let fileList: File[] = [];
      if (entry.isFile) {
        (entry as FileSystemFileEntry).file(async (file) => {
          let mimeType = file.type;
          if (!mimeType) {
            const arrayBuffer = await file.arrayBuffer();
            const fileType = await fileTypeFromBuffer(arrayBuffer);
            mimeType = fileType?.mime || 'application/octet-stream';
          }
          fileList.push(new File([file], `${path}${file.name}`, { type: mimeType }));
          resolve(fileList);
        }, reject);
      } else if (entry.isDirectory) {
        const reader = (entry as FileSystemDirectoryEntry).createReader();
        reader.readEntries((entries) => {
          const promises = entries.map((ent) => processEntry(ent, `${path}${entry.name}/`));
          Promise.all(promises)
            .then((results) => resolve(results.flat()))
            .catch(reject);
        }, reject);
      }
    });
  };

  /**
   * Traverses through a file tree starting from given items and returns a list of files.
   * Supports both `FileList` and `DataTransferItemList` inputs and can handle entries
   * via the webkitGetAsEntry method for directories.
   *
   * @param {FileList | DataTransferItemList} items - A list of file items to traverse.
   * @returns {Promise<File[]>} - A promise that resolves to an array of file objects.
   */
  const traverseFileTree = async (items: FileList | DataTransferItemList): Promise<File[]> => {
    let fileList: File[] = [];
    const promises = [];
    for (let i = 0; i < items.length; i++) {
      let entry: FileSystemEntry | null = null;
      const item = items[i];
      if (!item) {
        continue;
      }

      if ('webkitGetAsEntry' in item) {
        entry = (item as DataTransferItem).webkitGetAsEntry();
      } else if ('getAsFile' in item) {
        const file = (item as File).webkitRelativePath ? item : (item as File);
        if (file) {
          let mimeType = file.type;
          if (!mimeType) {
            const arrayBuffer = await file.arrayBuffer();
            const fileType = await fileTypeFromBuffer(arrayBuffer);
            mimeType = fileType?.mime || 'application/octet-stream';
          }
          fileList.push(
            new File([file], file.name, {
              type: mimeType,
              // originalFileName: file.name.split('/').pop(),
            }),
          );
        }
      }

      if (entry) {
        promises.push(processEntry(entry));
      }
    }
    const results = await Promise.all(promises);
    fileList = fileList.concat(...results.flat());

    return fileList;
  };

  /**
   * Builds a hierarchical file tree from an array of UploadFile objects.
   *
   * @param {UploadFile[]} files - The flat array of UploadFile objects to process.
   * @returns {UploadFile[]} The hierarchical file tree with correctly nested children.
   */
  function buildFileTreeFromUploadFiles(files: UploadFile[]): UploadFile[] {
    const fileMap = new Map<string, UploadFile>();

    // Step 1: Map all files by their id for quick lookup
    files.forEach((file) => {
      file.children = []; // Ensure children is initialized
      fileMap.set(file.id, file);
    });

    const root: UploadFile[] = [];

    // Step 2: Organize files into the tree
    files.forEach((file) => {
      if (file.parentUUID) {
        const parent = fileMap.get(file.parentUUID);
        if (parent) {
          parent.children!.push(file); // Add as a child to its parent
        }
      } else {
        root.push(file); // If no parent, it's a root node
      }
    });

    return root;
  }

  /**
   * Processes a file tree structure and returns a flattened list (queue) of files and folders for further processing.
   *
   * Updates each file or folder node in the tree with its parent's unique identifiers (`parentUid` and `parentUUID`)
   * and enqueues them into a flat array. If a folder node contains child nodes, they are traversed recursively
   * until all nodes in the structure are processed.
   *
   * @param {UploadFile[]} fileTree - An array representing the hierarchical structure of files and folders.
   * @param {number|null} [parentUid=null] - The unique identifier of the parent node in numerical format.
   * @param {string|null} [parentUUID=null] - The unique identifier of the parent node in string format.
   * @returns {UploadFile[]} - A flat array of all files and folders, with updated parent identifiers.
   */
  const getFilesToEnqueue = (
    fileTree: UploadFile[],
    parentUid: number | null = null,
    parentUUID: string | null = null,
  ): UploadFile[] => {
    const queue: UploadFile[] = [];
    const traverse = (nodes: UploadFile[], parentId: number | null, parentUUID: string | null) => {
      nodes.forEach((node) => {
        node.parentUid = parentId;
        node.parentUUID = parentUUID;
        queue.push(node);
        if (node.isFolder && node.children) {
          traverse(node.children, null, node.id);
        }
      });
    };
    traverse(fileTree, parentUid, parentUUID);
    return queue;
  };

  /**
   * Enqueues the given file tree to the upload queue, setting the parent ID for each file or folder.
   *
   * @param {UploadFile[]} fileTree - The hierarchical structure of files and folders to be enqueued.
   * @param {number|null} [parentUid=null] - The parent ID to be assigned to the files or folders, can be null if there's no parent.
   * @param parentUUID - The parent UUID to be assigned to the files or folders, can be null if there's no parent.
   */
  const enqueueFiles = (
    fileTree: UploadFile[],
    parentUid: number | null = null,
    parentUUID: string | null = null,
  ) => {
    const queue = getFilesToEnqueue(fileTree, parentUid, parentUUID);
    setUploadFilesQueue((prevQueue) => [...prevQueue, ...queue]);
  };

  /**
   * Registers a callback function to be invoked whenever a new file is uploaded.
   *
   * @param {function} callback - A function to be called upon the successful upload of a new file.
   * @param {FileInterface} callback.file - The file object that was uploaded.
   * @param {boolean} callback.isReplacing - Indicates if the uploaded file is replacing an existing file.
   */
  const onNewFileUploaded = (callback: (file: FileInterface, isReplacing: boolean) => void) => {
    newFileUploadedSubscribers.current.add(callback);
  };

  /**
   * Removes a previously registered callback for the new file uploaded event.
   *
   * @param {function} callback - The callback function to be removed. This function should accept a file object of type FileInterface and a boolean indicating if the file is replacing an existing file.
   */
  const offNewFileUploaded = (callback: (file: FileInterface, isReplacing: boolean) => void) => {
    newFileUploadedSubscribers.current.delete(callback);
  };

  /**
   * Handles the file uploaded event and notifies subscribers.
   *
   * This function marks the provided file as just uploaded by setting the
   * `isUnRead` property to `true`. It then invokes all registered
   * new file upload subscribers with the updated file object.
   *
   * @param {FileInterface} file - The file that has been uploaded.
   * @param {boolean} isReplacing - Indicates whether the uploaded file is replacing an existing file.
   */
  const handleFileUploaded = (file: FileInterface, isReplacing: boolean) => {
    const newUploadedFile = {
      ...file,
      isUnRead: true,
    };

    newFileUploadedSubscribers.current.forEach((callback) =>
      callback(newUploadedFile, isReplacing),
    );
  };

  /**
   * Updates the form field value, based on the field type.
   * @param {string} field - The name of the field to update.
   * @param {any} value - The new value for the field.
   * @returns {void}
   */
  const handleQuickShareInputChange = (field: keyof QuickShareFormState, value: any): void => {
    switch (field) {
      default:
        setQuickShareForm((prevForm) => ({ ...prevForm, [field]: value }));
        break;
    }
  };
  /**
   * Asynchronously retrieves the file tree structure from the provided list of files or items.
   *
   * This function takes a FileList or DataTransferItemList as input. If the input is an instance
   * of FileList, it converts it directly into an array of File objects. If the input is a
   * DataTransferItemList, the function processes the item list using the `traverseFileTree` method
   * to obtain the file structure.
   *
   * Once the files are retrieved, a file tree is constructed and returned by calling the
   * `buildFileTree` function.
   *
   * @param {FileList | DataTransferItemList} items - A collection of files or data transfer items
   *                                                  to process and build into a file tree.
   * @returns {Promise<Object>} A promise that resolves to the constructed file tree.
   */
  const getFileTree = async (items: FileList | DataTransferItemList) => {
    let files: File[] = [];
    if (items instanceof FileList) {
      files = Array.from(items);
    } else {
      files = await traverseFileTree(items);
    }
    files = files.filter((file) => !file.name.toLowerCase().endsWith('.shortcut'));
    return buildFileTree(files);
  };

  /**
   * Handles the uploading of files, either from a FileList or a DataTransferItemList.
   *
   * This function takes an input of files, processes them to ensure they're suitable for
   * uploading based on size, and then updates the upload queue with these files.
   *
   * @param {FileList | DataTransferItemList} items - The list of files to be uploaded.
   *
   * @param skipTreeBuild  - If true, skips the getFileTree step and goes straight to enqueue.
   * @param folderUid - The folder UID to upload the files to.
   * @param cufi
   * @returns {Promise<void>} A promise that resolves when the files have been successfully
   * added to the upload queue.
   */
  const uploadFiles = async (
    items: FileList | DataTransferItemList | UploadFile[],
    skipTreeBuild?: boolean,
    folderUid?: number,
    cufi?: string,
  ): Promise<void> => {
    // 1. If 'items' is already an array of UploadFile[] AND skipTreeBuild is true,
    // skip getFileTree and go straight to enqueue.
    if (Array.isArray(items) && skipTreeBuild) {
      currentFolderUid = folderUid || currentFolderUid;
      if (cufi) {
        items.forEach((item) => {
          item.cufi = cufi;
        });
      }
      enqueueFiles(items, folderUid || currentFolderUid);
      return;
    }

    // 2. Otherwise, continue with the original logic
    // (convert FileList/DataTransferItemList to fileTree).
    const fileTree = await getFileTree(items as FileList | DataTransferItemList);

    if (cufi) {
      fileTree.forEach((item) => {
        item.cufi = cufi;
      });
      currentFolderUid = folderUid || currentFolderUid;
      enqueueFiles(fileTree, folderUid || currentFolderUid);
      return;
    }

    if (currentFolderUid && isNaN(Number(currentFolderUid))) {
      // Check folder with fileService
      await fileService
        .getFile(currentFolderUid)
        .then((response) => {
          if (response?.isFolder) {
            currentFolderUid = response.uid;
          } else {
            notification.error({
              message: t('errors.invalidFolder'),
            });
            return;
          }
        })
        .catch(() => {
          notification.error({
            message: t('errors.invalidFolderUpload'),
          });
          setUploadFilesQueue([]);
          return;
        });
    } else if (folderUid) {
      currentFolderUid = folderUid;
    } else if (currentFolderUid) {
      currentFolderUid = parseInt(String(currentFolderUid));
    } else {
      currentFolderUid = null;
    }

    // 4. Verify if some files already exist in the folder
    const filesToCheck: string[] = fileTree.map((item) => item.fileName || '');
    let existingFiles = await fileService.checkFilesExists(
      filesToCheck,
      currentFolderUid || null,
      onBehalfOfUser?.id,
    );

    // 4a. Group existing files by name
    const groupedExistingFiles: Record<string, GroupedExistingFile> = existingFiles.reduce(
      (acc: Record<string, GroupedExistingFile>, file: FileInterface) => {
        const lowerTitle = file.fileName.toLowerCase();
        if (!acc[lowerTitle]) {
          acc[lowerTitle] = {
            ...file,
            quantity: 0,
          };
        }
        acc[lowerTitle].quantity += 1;
        return acc;
      },
      {},
    );

    // 5. If there are files already present, show the "Replace/Keep both" modal
    if (
      !(
        groupedExistingFiles &&
        Object.keys(groupedExistingFiles).length === 1 &&
        (Object.values(groupedExistingFiles)[0]?.quantity ?? 0) > 1
      )
    ) {
      if (existingFiles && existingFiles.length > 0) {
        EventBus.emit(EventBusEventEnum.IsGlobalLoaderVisible, {
          isVisible: false,
        });
        modal.open({
          title: t('modal.fileUploadOptionsForm.title', {
            count: existingFiles.length,
          }),
          message: t('modal.fileUploadOptionsForm.description', {
            resource: existingFiles[0]?.fileName,
            count: existingFiles.length,
          }),
          content: (
            <FileUploadOptionsForm
              onSubmit={(action) => {
                uploadOptionAction.current = action;
                uploadOptionGroupedExistingFiles.current = groupedExistingFiles;

                if (action === UploadFileActionEnum.NewOnly) {
                  uploadNewOnlyEntry(fileTree, currentFolderUid, fileService, enqueueFiles);
                } else if (action === UploadFileActionEnum.Replace) {
                  uploadNewOnlyWithReplaceOnExistingFilesEntry(
                    fileTree,
                    currentFolderUid,
                    fileService,
                    enqueueFiles,
                  );
                } else if (action === UploadFileActionEnum.KeepBoth) {
                  enqueueFiles(fileTree, folderUid || currentFolderUid);
                }

                modal.close(true);
              }}
              onCancel={() => {
                modal.close(true);
                return;
              }}
            />
          ),
        });
        return;
      }
    }

    EventBus.emit(EventBusEventEnum.IsGlobalLoaderVisible, {
      isVisible: false,
    });
    // 6. If no modal is shown, enqueue the files for upload
    enqueueFiles(fileTree, folderUid || currentFolderUid);
  };

  /**
   * @description Uploads a folder new only.
   * @param folder - The folder to upload.
   * @param parentId - The parent ID of the folder.
   * @param fileService - The file service to use.
   * @param onUploadEnqueue - The function to call when the folder is uploaded.
   */
  async function uploadFolderNewOnly(
    folder: UploadFile,
    parentId: number | null,
    fileService: FileService,
    onUploadEnqueue: (items: UploadFile[], parentId: number | null) => void,
  ) {
    let folderId: number;
    let folderCufi: string;

    if (!folder.fileName) {
      return;
    }

    // 1. Check if the folder already exists
    const existing = await fileService.checkFilesExists([folder.fileName], parentId);
    if (existing.length > 0 && existing[0]?.id && existing[0]?.cufi) {
      // If it exists, use these data
      folderId = existing[0].id;
      folderCufi = existing[0].cufi;
      folder.createdFile = existing[0];
    } else {
      // If it doesn't exist, create it
      const response = await fileService.createFolder({
        name: folder.fileName || '',
        parent: parentId?.toString() || '',
      });
      folderId = response.id;
      folderCufi = response.cufi;
      folder.createdFile = response;
    }

    // Handle direct files (NOT folders)
    if (folder.children && folder.children.length > 0) {
      const childFiles = folder.children.filter((f) => !f.isFolder);
      for (let i = 0; i < childFiles.length; i += 100) {
        const chunk = childFiles.slice(i, i + 100);
        const names = chunk.map((f) => f.fileName || '');
        // Check existing files in the right folder
        const existingFiles = await fileService.checkFilesExists(names, folderId);
        const existingNames = new Set(existingFiles.map((f) => f.fileName?.toLowerCase() || ''));
        // Take only the files that don't exist
        const filesToUpload = chunk.filter(
          (f) => !existingNames.has(f.fileName?.toLowerCase() || ''),
        );
        if (filesToUpload.length > 0) {
          filesToUpload.forEach((f) => {
            f.parentUid = folderId;
            f.parentCufi = folderCufi;
          });
          onUploadEnqueue(filesToUpload, folderId);
        }
      }
      // Recursive subfolders
      const childFolders = folder.children.filter((f) => f.isFolder);
      for (const subfolder of childFolders) {
        await uploadFolderNewOnly(subfolder, folderId, fileService, onUploadEnqueue);
      }
    }
  }

  /**
   * @description Uploads a new only entry.
   * @param fileTree - The file tree to upload.
   * @param parentId - The parent ID of the file tree.
   * @param fileService - The file service to use.
   * @param onUploadEnqueue - The function to call when the file tree is uploaded.
   */
  async function uploadNewOnlyEntry(
    fileTree: UploadFile[],
    parentId: number | null,
    fileService: FileService,
    onUploadEnqueue: (items: UploadFile[], parentId: number | null) => void,
  ) {
    // File root
    const rootFiles = fileTree.filter((f) => !f.isFolder);
    if (rootFiles.length > 0) {
      for (let i = 0; i < rootFiles.length; i += 100) {
        const chunk = rootFiles.slice(i, i + 100);
        const names = chunk.map((f) => f.fileName || '');
        const existing = await fileService.checkFilesExists(names, parentId);
        const existingNames = new Set(existing.map((f) => f.fileName?.toLowerCase() || ''));
        const filesToUpload = chunk.filter(
          (f) => !existingNames.has(f.fileName?.toLowerCase() || ''),
        );
        if (filesToUpload.length > 0) {
          onUploadEnqueue(filesToUpload, parentId);
        }
      }
    }

    // Root folders
    const rootFolders = fileTree.filter((f) => f.isFolder);
    for (const folder of rootFolders) {
      await uploadFolderNewOnly(folder, parentId, fileService, onUploadEnqueue);
    }
  }

  /**
   * @description Prepares a folder for upload, checking if it exists and creating it if it doesn't.
   * @param folder - The folder to prepare.
   * @param parentId - The parent ID of the folder.
   * @param parentCufi - The parent Cufi of the folder.
   * @param fileService - The file service to use.
   * @returns The prepared folder.
   */
  async function prepareFolderWithReplaceRecursive(
    folder: UploadFile,
    parentId: number | null,
    parentCufi: string | null,
    fileService: FileService,
  ): Promise<UploadFile> {
    let folderId: number;
    let folderCufi: string;

    if (!folder.fileName) return folder;

    const existing = await fileService.checkFilesExists([folder.fileName], parentId);
    if (existing.length > 0 && existing[0]?.id && existing[0]?.cufi) {
      folderId = existing[0].id;
      folderCufi = existing[0].cufi;
      folder.skipUpload = true;
      folder.createdFile = existing[0];
      folder.cufi = folderCufi;
      folder.id = generateUUID();
    } else {
      const created = await fileService.createFolder({
        name: folder.fileName,
        parent: parentId !== null && parentId !== undefined ? parentId.toString() : '',
      });
      folderId = created.id;
      folderCufi = created.cufi;
      folder.createdFile = created;
      folder.cufi = folderCufi;
      folder.id = generateUUID();
      folder.skipUpload = false;
    }
    folder.parentUid = parentId;
    folder.parentCufi = parentCufi;

    // 2. Ricorsione su figli
    if (folder.children && folder.children.length > 0) {
      const newChildren: UploadFile[] = [];
      for (const child of folder.children) {
        child.parentUid = folderId;
        child.parentCufi = folderCufi;
        child.parentUUID = folder.id;

        if (child.isFolder) {
          // Attenzione qui: passa NULL se non hai un id numerico valido
          let childIdNum = Number(child.id);
          const nextParentId =
            Number.isFinite(childIdNum) && child.id !== undefined && child.id !== ''
              ? childIdNum
              : folderId;

          const preparedFolder = await prepareFolderWithReplaceRecursive(
            child,
            nextParentId,
            child.cufi || folderCufi,
            fileService,
          );
          newChildren.push(preparedFolder);
        } else {
          // File: verifica se già esiste
          const existingChild = await fileService.checkFilesExists(
            [child.fileName || ''],
            folderId,
          );
          if (existingChild.length > 0 && existingChild[0]?.id) {
            child.cufi = existingChild[0].id?.toString();
          } else {
            child.cufi = undefined;
          }
          newChildren.push(child);
        }
      }
      folder.children = newChildren;
    }
    return folder;
  }

  /**
   * Entry point for the "newOnly + replace on found files" upload selection.
   */
  async function uploadNewOnlyWithReplaceOnExistingFilesEntry(
    fileTree: UploadFile[],
    parentId: number | null,
    fileService: FileService,
    onUploadEnqueue: (items: UploadFile[], parentId: number | null, parentCufi?: string) => void,
  ) {
    let allNodes: UploadFile[] = [];
    for (const item of fileTree) {
      if (item.isFolder) {
        const nodes = await prepareFolderWithReplaceRecursive(item, parentId, null, fileService);
        allNodes = allNodes.concat(nodes);
      } else {
        // Per i file root, check ed eventuale cufi
        if (!item.fileName) {
          continue;
        }
        const existing = await fileService.checkFilesExists([item.fileName], parentId);
        if (existing.length > 0 && existing[0]?.id) {
          item.cufi = existing[0].id.toString();
        } else {
          item.cufi = undefined;
        }
        item.parentUid = parentId;
        allNodes.push(item);
      }
    }
    // Ora la coda è completa, come fa il normale upload!
    onUploadEnqueue(allNodes, parentId);
  }

  /**
   * Manages the process of uploading items from the upload queue.
   * This function checks the upload queue for pending or failed uploads, and
   * processes them while respecting the limit of maximum concurrent uploads.
   * If the item to be uploaded is a folder, it creates the folder on the server
   * and updates the upload queue accordingly. If the item is a file, it uploads
   * the file in chunks and updates the queue with progress and other relevant details.
   *
   * The function handles retries for failed uploads, and ensures that the status
   * of the upload is updated accurately in the queue.
   *
   * @async
   * @function
   * @returns {void}
   */
  const uploadFileTask = async (file: UploadFile) => {
    // Security: don't start if it's already uploading, too late for retry or missing filename
    if (file.status === FileUploadStatusEnum.Uploading || file.retries >= 3 || !file.fileName) {
      return;
    }

    // Check size limits
    if (!file.isFolder && file.file) {
      const maxSizeBytes = fileSizeLimitUpload * 1024 * 1024; // MB -> Byte
      if (file.file.size > maxSizeBytes) {
        setUploadFilesQueue((prevQueue) =>
          prevQueue.map((u) =>
            u.id === file.id
              ? {
                  ...u,
                  status: FileUploadStatusEnum.Failed,
                  error: t('errors.fileSizeUploadLimit', {
                    value: fileSizeLimitUpload,
                  }),
                  retries: 3,
                }
              : u,
          ),
        );
        return;
      }
    }

    setUploadFilesQueue((prevQueue) =>
      prevQueue.map((u) =>
        u.id === file.id ? { ...u, status: FileUploadStatusEnum.Uploading } : u,
      ),
    );

    try {
      // Check if the file exists in the groupedExistingFiles map
      const lowerFileName = file.fileName.toLowerCase();
      const existingFile = uploadOptionGroupedExistingFiles.current?.[lowerFileName];
      const isReplacing =
        uploadOptionAction.current === UploadFileActionEnum.Replace && existingFile?.quantity === 1;

      if (file.isFolder) {
        if (file.skipUpload || isReplacing) {
          file.createdFile = existingFile;

          if (file.children) {
            file.children.forEach((child) => {
              child.parentCufi = file.createdFile?.cufi;
              child.parentUid = file.createdFile?.id;
            });
          }

          setUploadFilesQueue((prevQueue) => {
            // PATCH: If the folder is not in the queue, add it as completed!
            const folderExists = prevQueue.some((u) => u.id === file.id);
            if (!folderExists) {
              return [
                ...prevQueue,
                {
                  ...file,
                  status: FileUploadStatusEnum.Completed,
                  createdFile: existingFile,
                },
              ];
            } else {
              return prevQueue.map((u) =>
                u.id === file.id
                  ? {
                      ...u,
                      status: FileUploadStatusEnum.Completed,
                      createdFile: existingFile,
                    }
                  : u,
              );
            }
          });

          return;
        }

        // Create folder on server
        const newFolderPayload: CreateFolderPayload = {
          name: file.fileName,
          parent: file?.parentCufi?.toString() || currentFolderUid?.toString(),
        };
        if (onBehalfOfUser?.id) newFolderPayload.onBehalfOf = onBehalfOfUser.id;

        const response = await fileService.createFolder(newFolderPayload);
        file.createdFile = response;
        handleFileUploaded(response as FileInterface, isReplacing);

        // Update parentId of children
        if (file.children) {
          file.children.forEach((child) => {
            child.parentCufi = response.cufi;
            child.parentUid = response.id;
          });
        }

        // Mark as completed
        setUploadFilesQueue((prevQueue) =>
          prevQueue.map((u) =>
            u.id === file.id
              ? { ...u, status: FileUploadStatusEnum.Completed, createdFile: response }
              : u,
          ),
        );
      } else if (file.file) {
        const totalChunks = Math.ceil(file.file.size / CHUNK_SIZE);
        const totalSize = file.file.size;

        for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
          const start = chunkIndex * CHUNK_SIZE;
          const end = Math.min(start + CHUNK_SIZE, file.file.size);
          const chunk = file.file.slice(start, end);
          const blockId = `${chunkIndex}`;
          const formData = new FormData();
          formData.append('file', chunk, file.fileName);
          formData.append('blockId', blockId);
          formData.append('isLastChunk', chunkIndex === totalChunks - 1 ? 'true' : 'false');
          if (file.fileName) formData.append('filename', file.fileName);
          if (file.cufi) formData.append('cufi', file.cufi);
          else if (isReplacing && existingFile?.id)
            formData.append('cufi', existingFile.id.toString());
          if (file.parentUid) formData.append('parentId', String(file.parentUid));
          if (onBehalfOfUser?.id) formData.append('onBehalfOf', String(onBehalfOfUser.id));

          const response: UploadResponse = await transferService.upload(
            formData,
            (progressEvent) => {
              if (progressEvent.lengthComputable) {
                const bytesUploadedInChunk = progressEvent.loaded;
                const bytesUploadedBeforeChunk = chunkIndex * CHUNK_SIZE;
                const totalBytesUploaded = bytesUploadedBeforeChunk + bytesUploadedInChunk;
                const percentComplete = (totalBytesUploaded / totalSize) * 100;
                setUploadFilesQueue((prevQueue) =>
                  prevQueue.map((u) =>
                    u.id === file.id
                      ? {
                          ...u,
                          progress: Math.min(100, Math.floor(percentComplete)),
                        }
                      : u,
                  ),
                );
              }
            },
          );

          // Response
          if ('cufi' in response && 'version' in response) {
            if (response.cufi) {
              file.cufi = response.cufi;
              setUploadFilesQueue((prevQueue) =>
                prevQueue.map((u) => (u.id === file.id ? { ...u, cufi: response.cufi } : u)),
              );
            }
          } else {
            // Last chunk = FileInterface response
            setUploadFilesQueue((prevQueue) =>
              prevQueue.map((u) => (u.id === file.id ? { ...u, createdFile: response } : u)),
            );
            handleFileUploaded(response as FileInterface, isReplacing);
          }

          // Update progress on chunk
          setUploadFilesQueue((prevQueue) =>
            prevQueue.map((u) =>
              u.id === file.id
                ? {
                    ...u,
                    progress: Math.floor(((chunkIndex + 1) / totalChunks) * 100),
                    chunksUploaded: chunkIndex + 1,
                    totalChunks,
                  }
                : u,
            ),
          );
        }

        // Mark as completed
        setUploadFilesQueue((prevQueue) =>
          prevQueue.map((u) =>
            u.id === file.id ? { ...u, status: FileUploadStatusEnum.Completed } : u,
          ),
        );
      }
    } catch (error: any) {
      // Failure and retry
      if (file.isFolder) {
        setUploadFilesQueue((prevQueue) =>
          prevQueue.map((u) =>
            u.status === FileUploadStatusEnum.Pending || u.status === FileUploadStatusEnum.Uploading
              ? {
                  ...u,
                  status: FileUploadStatusEnum.Failed,
                  retries: 3,
                  error: t('errors.uploadingFileError'),
                }
              : u,
          ),
        );
        return;
      }
      setUploadFilesQueue((prevQueue) =>
        prevQueue.map((u) =>
          u.id === file.id
            ? {
                ...u,
                retries: u.retries + 1,
                status: FileUploadStatusEnum.Failed,
                error: u.retries < 3 ? t('errors.uploadingFileError') : error,
              }
            : u,
        ),
      );
      Sentry.captureException(error);
    }
  };

  /**
   * Tries to start uploads based on the current upload queue.
   *
   * This function checks the upload queue for pending or failed uploads that have not exceeded
   * the maximum number of retries (3). It then starts uploading the files in the queue, up to the
   * number of available slots (MAX_CONCURRENT_UPLOADS).
   */
  function tryStartUploads() {
    const activeUploads = uploadFilesQueue.filter(
      (u) => u.status === FileUploadStatusEnum.Uploading,
    );
    const pendingUploads = uploadFilesQueue.filter(
      (u) =>
        (u.status === FileUploadStatusEnum.Pending || u.status === FileUploadStatusEnum.Failed) &&
        u.retries < 3,
    );

    const readyToUpload = pendingUploads.filter((file) => {
      // If the file has parentUid but the parent does not exist in the queue, consider it ready (case: Quick Share external root folder)
      if (file.parentUid && !uploadFilesQueue.some((u) => u.id === String(file.parentUid))) {
        return true;
      }
      // Old logic
      if (!file.parentUUID && !file.parentUid) return true;
      const parent = uploadFilesQueue.find(
        (u) => u.id === file.parentUUID || u.id === String(file.parentUid),
      );
      return parent && parent.status === FileUploadStatusEnum.Completed;
    });
    const slotsAvailable = MAX_CONCURRENT_UPLOADS - activeUploads.length;
    readyToUpload.slice(0, slotsAvailable).forEach((uploadFile) => {
      uploadFileTask(uploadFile);
    });
  }

  /**
   * Checks if a given file is inside a folder.
   *
   * @param {UploadFile} file - The file to be checked.
   * @returns {boolean} - Returns `true` if the file is inside a folder, otherwise `false`.
   */
  const checkIfFileIsInsideFolder = (file: UploadFile): boolean => {
    return (
      file.parentCufi !== null &&
      file.parentCufi !== undefined &&
      file.parentCufi !== '' &&
      !file.isFolder
    );
  };

  /**
   * Retrieves all files with a "Failed" status within a specified folder and its subfolders.
   *
   * @param {string} folderUUID - The UUID of the root folder to search within.
   * @returns {UploadFile[]} An array containing files that have a "Failed" status.
   */
  const getFailedFilesInFolder = (folderUUID: string): UploadFile[] => {
    const failedFiles: UploadFile[] = [];

    /**
     * Recursive function to traverse the folder structure.
     *
     * @param {string} currentUUID - The UUID of the current folder.
     */
    const traverse = (currentUUID: string) => {
      // Find all files and folders that have currentUUID as parentUUID
      const children = uploadFilesQueue.filter((u) => u.parentUUID === currentUUID);

      children.forEach((child) => {
        if (child.isFolder) {
          traverse(child.id);
        } else {
          if (child.status === FileUploadStatusEnum.Failed) {
            failedFiles.push(child);
          }
        }
      });
    };

    traverse(folderUUID);

    return failedFiles;
  };

  /**
   * Groups a list of upload items into a hierarchical structure, aggregating progress, status,
   * and other relevant information for folder items based on their children.
   *
   * @param {UploadFile[]} uploadFilesQueue - A list of upload file items, where each item represents
   * either a file or a folder containing related metadata such as status, progress, and parent-child relationships.
   * @param onlyQuickShare
   * @param startDepth
   * @return {GroupedUploadItem[]} A grouped list of upload items, where folders contain aggregated
   * data (e.g., total files, uploaded files, cumulative progress) and their status is determined based
   * on the statuses of contained files or subfolders.
   */
  function groupUploadItems(
    uploadFilesQueue: UploadFile[],
    onlyQuickShare = false,
    startDepth = 0,
  ): GroupedUploadItem[] {
    /**
     * Recursive function to calculate information about files and folders.
     * Returns an array of GroupedUploadItem related to folders or files
     * that are exactly at a certain depth.
     *
     * @param {string | null} parentUUID - The UUID of the parent to start from.
     * @param {number} depth - The current depth.
     * @returns {GroupedUploadItem[]} An array of items grouped at this depth.
     */
    function collectItemsAtDepth(parentUUID: string | null, depth: number): GroupedUploadItem[] {
      // Filter the child elements of the current parent (based on parentUUID)
      let childItems: UploadFile[] = [];
      if (onlyQuickShare) {
        childItems = uploadFilesQueue.filter(
          (item) => item.parentUUID === parentUUID && item.isQuickShare,
        );
      } else {
        childItems = uploadFilesQueue.filter(
          (item) => item.parentUUID === parentUUID && !item.isQuickShare,
        );
      }

      // If the current depth is less than startDepth, go deeper in the hierarchy
      // without adding the current elements, waiting to reach startDepth.
      if (depth < startDepth) {
        // Move forward in the children hierarchy
        const deeperItems: GroupedUploadItem[] = [];
        for (const item of childItems) {
          if (item.isFolder) {
            // Recursion on the children of the folder
            deeperItems.push(...collectItemsAtDepth(item.id, depth + 1));
          } else {
            // Even if we find a file here, we are at a level lower than startDepth, so
            // we do not include it.
          }
        }
        return deeperItems;
      }

      // If we are at least at startDepth, start transforming the elements into GroupedUploadItem
      const items: GroupedUploadItem[] = [];

      // Function to collect info about a folder
      function collectFolderFiles(folderUUID: string): {
        total: number;
        totalSize: number;
        uploaded: number;
        cumulativeProgress: number;
        statuses: Set<FileUploadStatusEnum>;
      } {
        let total = 0;
        let totalSize = 0;
        let uploaded = 0;
        let cumulativeProgress = 0;
        const statuses = new Set<FileUploadStatusEnum>();

        let folderChildItems = uploadFilesQueue.filter((item) => item.parentUUID === folderUUID);

        if (onlyQuickShare) {
          folderChildItems = folderChildItems.filter((item) => item.isQuickShare);
        }

        for (const item of folderChildItems) {
          if (item.isFolder) {
            const subFolder = collectFolderFiles(item.id);
            total += subFolder.total;
            totalSize += subFolder.totalSize;
            uploaded += subFolder.uploaded;
            cumulativeProgress += subFolder.cumulativeProgress;
            subFolder.statuses.forEach((s) => statuses.add(s));
          } else {
            total += 1;
            totalSize += item.file?.size || 0;
            statuses.add(item.status);
            cumulativeProgress += item.progress || 0;
            if (item.status === FileUploadStatusEnum.Completed) {
              uploaded += 1;
            }
          }
        }
        return { total, totalSize, uploaded, cumulativeProgress, statuses };
      }

      // Transform the children at this depth into GroupedUploadItem
      for (const item of childItems) {
        if (item.isFolder) {
          const { total, totalSize, uploaded, cumulativeProgress, statuses } = collectFolderFiles(
            item.id,
          );

          const progress = total > 0 ? Math.floor(cumulativeProgress / total) : 0;

          let folderStatus: FileUploadStatusEnum;
          if (
            statuses.has(FileUploadStatusEnum.Pending) ||
            statuses.has(FileUploadStatusEnum.Uploading)
          ) {
            if (item.isQuickShare) {
              folderStatus = FileUploadStatusEnum.Pending;
            } else {
              folderStatus = FileUploadStatusEnum.Uploading;
            }
          } else if (statuses.has(FileUploadStatusEnum.Failed)) {
            folderStatus = FileUploadStatusEnum.Failed;
          } else {
            folderStatus = FileUploadStatusEnum.Completed;
          }

          items.push({
            id: item.id,
            isFolder: true,
            fileName: item.fileName || '',
            fileExtension: '',
            status: folderStatus,
            totalFiles: total,
            uploadedFiles: uploaded,
            fileSize: totalSize,
            progress: progress,
            createdFile: item.createdFile,
            retries: item.retries,
          });
        } else {
          const progress = item.progress || 0;
          const fileExtension = item.fileName?.split('.').pop() || '';

          items.push({
            id: item.id,
            isFolder: false,
            fileName: item.fileName || '',
            fileExtension: fileExtension || item?.createdFile?.fileExtension || '',
            status: item.status,
            fileSize: item.file?.size || 0,
            progress,
            createdFile: item.createdFile,
            retries: item.retries,
            totalFiles: 1,
            uploadedFiles: item.status === FileUploadStatusEnum.Completed ? 1 : 0,
          });
        }
      }
      return items;
    }

    // Start from the top-level (parentUUID == null) with depth = 0
    // The recursive function will go down the levels and filter
    // the elements based on startDepth.
    return collectItemsAtDepth(null, 0);
  }

  /**
   * Retries the upload process for files that previously failed in the upload queue.
   *
   * @param {boolean} [onlyQuickShare=false] - If set to true, only retries failed uploads
   * for files marked as quick-share. Defaults to false, attempting to retry all failed uploads.
   *
   * This function updates the file upload queue by resetting the status of failed uploads
   * to `Pending` and their retry count to 0. Files that do not match the specified criteria
   * remain unchanged in the queue.
   */
  const retryFailedUploads = (onlyQuickShare: boolean = false): void => {
    setUploadFilesQueue((prevQueue) =>
      prevQueue.map((file) => {
        if (file.isQuickShare === onlyQuickShare && file.status === FileUploadStatusEnum.Failed) {
          return {
            ...file,
            status: FileUploadStatusEnum.Pending,
            retries: 0,
          };
        }
        return file;
      }),
    );
  };

  /**
   * Handles the task of emptying the current upload files queue.
   *
   * This function resets the upload files queue to an empty array. It is typically used
   * when there is a need to clear any pending or uploaded files from the queue.
   */
  const handleEmptyUploadFilesQueue = () => {
    setUploadFilesQueue((prevQueue) => prevQueue.filter((file) => file.isQuickShare));
  };

  /**
   * Filters and updates the upload queue to retain only files marked for Quick Share.
   *
   * This function is responsible for clearing non-Quick Share files from the uploadFilesQueue state.
   * It ensures that only files with the `isQuickShare` property set to `true` remain in the queue.
   *
   * The state is updated using a filtering mechanism that removes files not marked as Quick Share.
   */
  const handleEmptyQuickShareUploadFilesQueue = () => {
    setQuickShareForm({
      users: [],
      groups: [],
      permission: '0',
      expiration: '1',
      hasMessage: true,
      message: '',
    });
    setUploadFilesQueue((prevQueue) => prevQueue.filter((file) => !file.isQuickShare));
  };

  /**
   * Handles the quick share process for selected files.
   * This function gathers file identifiers from the current upload queue, determines expiration settings,
   * and prepares a payload to update access permissions for the provided files and recipients.
   * It also displays notifications for success or failure of the operation.
   *
   * Behavior:
   * - Collects file IDs from the `uploadQuickShareGroupedFilesQueue`.
   * - Converts expiration information to a timestamp in ticks (milliseconds) if an expiration date is provided in the form.
   * - Compiles recipient details from users and groups listed in the form data.
   * - Configures limitations such as read-only or anti-capture restrictions based on form settings.
   * - Includes an optional message if a message is specified in the form.
   *
   * If the operation is successful:
   * - Displays a success notification to the user.
   * - Clears the quick share upload files queue.
   *
   * If the operation fails:
   * - Displays an error notification to the user.
   */
  const handleShareQuickShareFiles = () => {
    const fileIds = uploadQuickShareGroupedFilesQueue
      .map((f) => f.createdFile?.cufi)
      .filter(Boolean) as string[];

    // Convert the days string (e.g., "7") to a number (7)
    const days = quickShareForm?.expiration ? parseInt(quickShareForm.expiration, 10) : 0;

    // Add 'days' days to the current date
    const expirationDate = new Date(Date.now() + days * 24 * 60 * 60 * 1000);

    // Convert the expiration date to ticks
    const expirationTicks = dateToTicks(expirationDate);

    const request: UpdateFilesAccessPayload = {
      // Create an array of object with users and groups
      allRecipients: [
        ...quickShareForm?.users?.map((u) => ({
          recipient: u.email,
          isGroup: false,
          limitations: {
            expiry: expirationTicks,
            readOnly: quickShareForm?.permission === '0',
          },
        })),
        ...quickShareForm?.groups?.map((g) => ({
          recipient: g.name,
          isGroup: true,
          limitations: {
            expiry: expirationTicks,
            readOnly: quickShareForm?.permission === '0',
          },
        })),
      ],
      files: fileIds,
      limitations: {
        //antiCapture: false,
        expiry: expirationTicks,
        readOnly: quickShareForm?.permission === '0',
      },
    };
    // Add message if showMessageBox is true and messageForNewShare is not empty
    if (quickShareForm?.hasMessage && quickShareForm?.message?.trim() !== '') {
      request.message = quickShareForm.message;
    } else if (quickShareForm?.hasMessage) {
      request.message = '';
    } else {
      request.message = null;
    }

    fileService
      .updateFilesAccess(request)
      .then(() => {
        notification.success({
          message: t('notifications.quickShareSuccess'),
        });
        setUploadFilesQueue((prevQueue) =>
          prevQueue.map((file) =>
            file.isQuickShare
              ? {
                  ...file,
                  status:
                    file.status === FileUploadStatusEnum.FailedSharing
                      ? FileUploadStatusEnum.Completed
                      : file.status,
                }
              : file,
          ),
        );

        //handleEmptyQuickShareUploadFilesQueue();
      })
      .catch(() => {
        // Update the status of the files in the queue with FailedSharing status for quickShare files
        setUploadFilesQueue((prevQueue) =>
          prevQueue.map((file) =>
            file.isQuickShare
              ? {
                  ...file,
                  status:
                    file.status === FileUploadStatusEnum.Completed
                      ? FileUploadStatusEnum.FailedSharing
                      : file.status,
                }
              : file,
          ),
        );

        notification.error({
          message: t('errors.quickShareError'),
        });
      });
  };

  /**
   * A function to handle the display of the Uploading Files Errors Modal.
   *
   * @function
   * @param {boolean} [onlyQuickShareFiles=false] - A flag indicating whether to show only errors for quick share files.
   * @returns {void}
   * @param onlyQuickShareFiles
   */
  const handleOpenUploadingFilesErrorsModal = (onlyQuickShareFiles: boolean): void => {
    setShowOnlyQuickShareFilesUploadingErrors(onlyQuickShareFiles);
    modal.open({
      title: t('common.uploadErrors'),
      message: t('common.uploadErrorsDescription'),
      content: (
        <FilesUploadErrorForm
          uploadFiles={failedUploadFiles}
          onCancel={() => {
            setShowOnlyQuickShareFilesUploadingErrors(false);
            modal.close(true);
          }}
        />
      ),
    });
  };

  /**
   * The number of files currently in the uploading or pending state.
   * This variable is derived from filtering the uploadFilesQueue based on
   * the file statuses: Uploading and Pending.
   *
   * Note: The value is memoized using `useMemo` to optimize performance by
   * recalculating only when `uploadFilesQueue` changes.
   *
   * @type {number}
   */
  const uploadingFilesAmount: number = useMemo(() => {
    return uploadFilesQueue.filter((file) => file.status === FileUploadStatusEnum.Uploading).length;
  }, [uploadFilesQueue]);

  /**
   * A memoized variable that calculates the number of successfully uploaded files.
   *
   * This variable derives its value by filtering through the `uploadFilesQueue`
   * and counting how many files have a status of `Completed` as defined
   * in the `FileUploadStatusEnum`.
   *
   * Dependencies:
   * - `uploadFilesQueue`: An array of files currently in the upload queue.
   *
   * @return {number} The number of files that have been uploaded successfully.
   */
  const uploadedFilesAmount: number = useMemo(() => {
    return uploadFilesQueue.filter((file) => file.status === FileUploadStatusEnum.Completed).length;
  }, [uploadFilesQueue]);

  /**
   * A boolean indicator to check if there are currently any files being uploaded.
   *
   * The value is derived using a memoized function that iterates through the
   * `uploadFilesQueue` to determine if any files have statuses of either
   * `Uploading` or `Pending`.
   *
   * Dependencies:
   * - `uploadFilesQueue`: The queue of files currently being processed for upload.
   * - `FileUploadStatusEnum`: An enumeration that defines possible file upload statuses.
   */
  const isUploadingFiles = useMemo(() => {
    return uploadFilesQueue.some(
      (file) =>
        file.status === FileUploadStatusEnum.Uploading ||
        file.status === FileUploadStatusEnum.Pending,
    );
  }, [uploadFilesQueue]);

  /**
   * A memoized boolean variable indicating if there are any files in the upload queue
   * that are being uploaded or are pending upload with the "Quick Share" status.
   *
   * The value is derived by checking the `uploadFilesQueue` to see if any file
   * in the queue has a status of either `Uploading` or `Pending`, and also has
   * the `isQuickShare` property set to true. This ensures that it specifically
   * tracks the upload status of Quick Share files.
   *
   * Dependencies:
   * - Relies on the current state of `uploadFilesQueue` for calculation.
   */
  const isUploadingQuickShareFiles = useMemo(() => {
    return uploadFilesQueue.some(
      (file) =>
        (file.status === FileUploadStatusEnum.Uploading ||
          file.status === FileUploadStatusEnum.Pending) &&
        file.isQuickShare,
    );
  }, [uploadFilesQueue]);

  /**
   * Determines if there are any folders present in the upload files queue.
   *
   * @type {boolean}
   *
   * The `areFoldersPresentsInFilesQueue` variable uses `useMemo` to efficiently
   * compute whether any file in the `uploadFilesQueue` is a folder. It leverages
   * the `some` method to check if at least one file object within the queue has
   * the `isFolder` property set to `true`.
   *
   * Dependencies:
   * - `uploadFilesQueue`: An array of file objects, where each object may
   *   contain an `isFolder` property indicating if the file is a folder.
   */
  const areFoldersPresentsInFilesQueue: boolean = useMemo(() => {
    return uploadFilesQueue.some((file) => file.isFolder);
  }, [uploadFilesQueue]);

  /**
   * A memoized array of grouped upload items generated from `uploadFilesQueue`.
   * Each item in the queue can either be a file or a folder containing files,
   * and this array provides a simplified structure to represent them along with their upload status.
   *
   * The variable contains `GroupedUploadItem` objects, which include details like
   * the file name, extension, upload status, and progress percentage. Special handling
   * is given to folders to recursively collect files and compute upload progress.
   *
   * @type {GroupedUploadItem[]}
   */
  const uploadGroupedFilesQueue: GroupedUploadItem[] = useMemo(() => {
    return groupUploadItems(uploadFilesQueue);
  }, [uploadFilesQueue]);

  /**
   * Represents a memoized array of grouped upload items specifically for the quick share feature.
   * This variable utilizes the `groupUploadItems` function to group files from the `uploadFilesQueue`
   * and provides an optimized, recalculated value only when `uploadFilesQueue` changes.
   *
   * The grouping logic applied helps organize the file upload process more efficiently for quick sharing scenarios.
   *
   * @type {GroupedUploadItem[]}
   */
  const uploadQuickShareGroupedFilesQueue: GroupedUploadItem[] = useMemo(() => {
    return groupUploadItems(uploadFilesQueue, true);
  }, [uploadFilesQueue]);

  /**
   * A memoized array of grouped upload items specifically for the
   * quick share feature, excluding any root folder. This variable stores
   * the grouped files intended for uploading and ensures efficient rendering
   * and state management by recalculating only when the upload queue changes.
   *
   * The grouping logic for the items is handled by the `groupUploadItems` function,
   * where it processes the provided upload files queue and applies the specified
   * criteria (e.g., ignoring the root folder).
   *
   * Dependencies:
   * - `uploadFilesQueue`: An array of files or items queued for upload.
   *
   * Type:
   * - `GroupedUploadItem[]`: An array consisting of the grouped upload items.
   */
  const uploadQuickShareGroupedFilesQueueWithoutRootFolder: GroupedUploadItem[] = useMemo(() => {
    const filteredUploadFilesQueue = uploadFilesQueue.filter((file) => file.isQuickShare);
    let offset = 1;
    if (filteredUploadFilesQueue.length === 1) {
      offset = 0;
    }

    return groupUploadItems(uploadFilesQueue, true, offset);
  }, [uploadFilesQueue]);

  /**
   * The total number of files across all grouped file uploads.
   *
   * This variable computes the sum of the total files from each item in the
   * uploadGroupedFilesQueue. It uses the useMemo hook to memoize the computed
   * value, ensuring it only recalculates when the uploadGroupedFilesQueue array
   * changes.
   *
   * @type {number}
   */
  const totalGroupedFilesAmount: number = useMemo(() => {
    return uploadGroupedFilesQueue.reduce((acc, item) => {
      return acc + item.totalFiles;
    }, 0);
  }, [uploadGroupedFilesQueue]);

  /**
   * Represents the total number of files in the grouped files queue for Quick Share.
   *
   * This value is determined using the `uploadQuickShareGroupedFilesQueue` array
   * by summing up the `totalFiles` property of each item in the queue. It recalculates
   * whenever the `uploadQuickShareGroupedFilesQueue` changes.
   *
   * @type {number}
   */
  const totalQuickShareGroupedFilesAmount: number = useMemo(() => {
    return uploadQuickShareGroupedFilesQueue.reduce((acc, item) => {
      return acc + item.totalFiles;
    }, 0);
  }, [uploadQuickShareGroupedFilesQueue]);

  /**
   * Represents the total number of files that have been uploaded across all grouped queues.
   * The value is derived by summing up the `uploadedFiles` from each item in the `uploadGroupedFilesQueue`.
   *
   * This is a memoized value that updates only when the `uploadGroupedFilesQueue` changes.
   */
  const uploadedGroupedFilesAmount: number = useMemo(() => {
    return uploadGroupedFilesQueue.reduce((acc, item) => {
      return acc + item.uploadedFiles;
    }, 0);
  }, [uploadGroupedFilesQueue]);

  /**
   * Represents the total number of files that have been successfully uploaded
   * within the "Quick Share Grouped Files" queue. This is computed by summing
   * the `uploadedFiles` property of each item in the queue.
   *
   * The value is memoized to optimize performance and will only be recalculated
   * when the `uploadQuickShareGroupedFilesQueue` changes.
   */
  const uploadedQuickShareGroupedFilesAmount: number = useMemo(() => {
    return uploadQuickShareGroupedFilesQueue.reduce((acc, item) => {
      return acc + item.uploadedFiles;
    }, 0);
  }, [uploadQuickShareGroupedFilesQueue]);

  /**
   * The total number of failed files in the grouped upload queue.
   *
   * This computed value sums the total number of files within failed folder items and adds individual failed files.
   * It uses the `useMemo` hook to optimize performance by memoizing the computed result, recalculating only when the `uploadGroupedFilesQueue` changes.
   */
  const failedGroupedFilesAmount: number = useMemo(() => {
    return uploadGroupedFilesQueue.reduce((acc, item) => {
      if (item.isFolder && item.status === FileUploadStatusEnum.Failed) {
        return acc + item.totalFiles;
      } else if (!item.isFolder && item.status === FileUploadStatusEnum.Failed) {
        return acc + 1;
      }
      return acc;
    }, 0);
  }, [uploadGroupedFilesQueue]);

  /**
   * A memoized computed variable that filters and returns the list of files
   * from the upload queue which have a failed upload status.
   *
   * This variable depends on the current state of `uploadFilesQueue`. Any changes
   * to the `uploadFilesQueue` will cause `failed*/
  const failedUploadFiles = useMemo(() => {
    return uploadFilesQueue.filter(
      (file) =>
        file.status === FileUploadStatusEnum.Failed &&
        file.isQuickShare === showOnlyQuickShareFilesUploadingErrors,
    );
  }, [uploadFilesQueue, showOnlyQuickShareFilesUploadingErrors]);

  /**
   * A memoized variable that provides a filtered list of files from the upload queue with a specific status and attribute.
   *
   * This variable contains only the files that meet the following conditions:
   * - The `status` of the file is `Failed` (as defined by the `FileUploadStatusEnum.Failed` enumeration value).
   * - The file has the `isQuickShare` attribute set to `true`.
   *
   * The filtering operation is achieved using the `uploadFilesQueue` as the source data.
   * Changes to the `uploadFilesQueue` will trigger recalculation of this variable.
   *
   * Dependencies:
   * - This variable is recomputed whenever the `uploadFilesQueue` changes.
   *
   * Intended use:
   * To identify and handle quick share files that failed during the upload process.
   *
   * @type {Array} Filtered list of quick share files with failed upload status.
   */
  const failedUploadQuickShareFiles: Array<UploadFile> = useMemo(() => {
    return uploadFilesQueue.filter(
      (file) => file.status === FileUploadStatusEnum.Failed && file.isQuickShare,
    );
  }, [uploadFilesQueue]);

  /**
   * A boolean variable that indicates whether all files in the `uploadFilesQueue`
   * marked for Quick Share have been successfully uploaded without any errors.
   *
   * The variable checks if the `uploadFilesQueue` contains files and ensures
   * that all files fulfilling the Quick Share condition have a status of
   * "Completed" in the `FileUploadStatusEnum`.
   *
   * This value is memoized based on the `uploadFilesQueue` dependency, meaning
   * it will only recompute when the `uploadFilesQueue` changes.
   */
  const isQuickShareUploadFilesCompletedWithoutErrors = useMemo(() => {
    return (
      uploadFilesQueue.length > 0 &&
      uploadFilesQueue.every(
        (file) => file.isQuickShare && file.status === FileUploadStatusEnum.Completed,
      )
    );
  }, [uploadFilesQueue]);

  /**
   * Calculates the global progress percentage of quick share files based on the total bytes uploaded.
   *
   * This function filters the upload queue to include only quick share files that are not folders
   * and have a file associated with them. It then calculates the total bytes of all these files and
   * the bytes that have been uploaded. The progress percentage is computed by dividing the uploaded
   * bytes by the total bytes and multiplying by 100.
   *
   * @type {number} The global progress percentage of quick share files.
   */
  const quickShareGlobalProgressByBytes = useMemo(() => {
    // Calculate the percentage of bytes already uploaded compared to the total
    const files = uploadFilesQueue.filter((f) => f.isQuickShare && !f.isFolder && f.file);
    const totalBytes = files.reduce((acc, file) => acc + (file.file?.size || 0), 0);
    const uploadedBytes = files.reduce(
      (acc, file) => acc + Math.floor(((file.progress || 0) / 100) * (file.file?.size || 0)),
      0,
    );

    if (totalBytes === 0) return 0;
    return Math.floor((uploadedBytes / totalBytes) * 100);
  }, [uploadFilesQueue]);

  useEffect(() => {
    if (!isLoggedIn) {
      handleEmptyQuickShareUploadFilesQueue();
      handleEmptyUploadFilesQueue();
    }

    // Check if all enqueued files with isQuickShare flag are uploaded correctly
    const areAllQuickShareFilesUploaded =
      uploadFilesQueue.length > 0 &&
      uploadFilesQueue.every(
        (file) => file.isQuickShare && file.status === FileUploadStatusEnum.Completed,
      );

    // Check if quick share file upload is completed without errors when all files are uploaded with status completed
    if (areAllQuickShareFilesUploaded) {
      handleShareQuickShareFiles();
    }
  }, [
    isLoggedIn,
    isUploadingQuickShareFiles,
    uploadedQuickShareGroupedFilesAmount,
    totalQuickShareGroupedFilesAmount,
  ]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      const isUploading = uploadFilesQueue.some(
        (file) =>
          file.status === FileUploadStatusEnum.Pending ||
          file.status === FileUploadStatusEnum.Uploading,
      );
      if (isUploading) {
        e.preventDefault();
        // For older browsers
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [uploadFilesQueue]);

  useEffect(() => {
    tryStartUploads();
    // Every time the queue changes, try to start new uploads
  }, [uploadFilesQueue]);

  return (
    <FileUploadContext.Provider
      value={{
        uploadFiles,
        uploadGroupedFilesQueue,
        uploadQuickShareGroupedFilesQueue,
        uploadQuickShareGroupedFilesQueueWithoutRootFolder,
        uploadFilesQueue,
        uploadingFilesAmount,
        uploadedFilesAmount,
        totalGroupedFilesAmount,
        totalQuickShareGroupedFilesAmount,
        uploadedGroupedFilesAmount,
        uploadedQuickShareGroupedFilesAmount,
        failedGroupedFilesAmount,
        isUploadingFiles,
        isUploadingQuickShareFiles,
        areFoldersPresentsInFilesQueue,
        buildFileTreeFromUploadFiles,
        getFilesToEnqueue,
        groupUploadItems,
        getFileTree,
        getFailedFilesInFolder,
        isFileInsideFolder: checkIfFileIsInsideFolder,
        emptyUploadFilesQueue: handleEmptyUploadFilesQueue,
        emptyQuickShareUploadFilesQueue: handleEmptyQuickShareUploadFilesQueue,
        onNewFileUploaded,
        offNewFileUploaded,
        quickShareForm,
        handleQuickShareInputChange,
        handleShareQuickShareFiles,
        failedUploadQuickShareFiles,
        handleOpenUploadingFilesErrorsModal,
        retryFailedUploads,
        isQuickShareUploadFilesCompletedWithoutErrors,
        setCurrentFolderUid,
        quickShareGlobalProgressByBytes,
      }}
    >
      {children}
    </FileUploadContext.Provider>
  );
};
